import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import BlogCTAButtons from "@/components/BlogCTAButtons";
import CanonicalTag from "@/components/CanonicalTag";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import heroImage from "@/assets/indoor-outdoor-adu-living.png";

const BlogIndoorOutdoorADU = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "Indoor Outdoor ADU Living: How to Design Spaces That Feel Open and Connected",
    "description": "Large openings, seamless transitions, and usable outdoor space. Learn how indoor outdoor design makes ADUs feel larger and more livable.",
    "datePublished": "2026-04-15",
    "author": { "@type": "Organization", "name": "UDA ADU" },
    "publisher": { "@type": "Organization", "name": "UDA ADU" },
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/blogs/indoor-outdoor-adu-living" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <Navigation />
      <div className="w-full max-w-5xl mx-auto px-4 pt-8">
        <img src={heroImage} alt="Indoor outdoor ADU living with seamless transition" className="w-full rounded-2xl" />
      </div>
      <main className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/blogs" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Blog
        </Link>
        <p className="text-sm text-muted-foreground mb-2">April 15, 2026</p>
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-8">Indoor Outdoor ADU Living: How to Design Spaces That Feel Open and Connected</h1>
        <article className="prose prose-lg max-w-none text-muted-foreground space-y-6">
          <p>Most ADUs feel closed.</p>
          <p>You walk in, and everything stays inside. Walls define the space. The outside feels separate.</p>
          <p>That is the old way of building.</p>
          <p>Today, the best ADUs are designed to open up.</p>
          <p>Indoor outdoor living is not about adding a patio. It starts with how the entire space is planned. When done right, the ADU feels larger, brighter, and more comfortable without increasing the square footage.</p>
          <p>At UDA ADU, this starts with openings.</p>
          <p>Large sliding or folding doors change everything. When the living area connects directly to the outside, the space extends beyond the walls. The patio becomes part of the home.</p>
          <p>That shift is immediate.</p>
          <p>Natural light plays a major role.</p>
          <p>Wide glass openings, combined with well-placed windows, bring light deep into the space. This removes shadows and makes even smaller units feel open and breathable.</p>
          <p>But light alone is not enough.</p>
          <p>The transition between inside and outside needs to feel smooth. Flooring that aligns closely in tone or level helps connect both areas. When there is no visual break, the space reads as one continuous environment.</p>
          <p>Outdoor design matters just as much.</p>
          <p>A clean patio, simple seating, and light landscaping create a usable extension of the living area. It does not need to be complex. It needs to feel intentional and easy to use.</p>
          <p>Privacy still stays in focus.</p>
          <p>Even with open designs, placement and screening ensure the space feels comfortable. Fencing, greenery, or strategic orientation keeps the openness without exposure.</p>
          <p>Inside the ADU, layout supports the concept.</p>
          <p>The living room should lead directly to the outdoor area. Kitchens positioned nearby make the space more functional. This creates a natural flow for daily use, not just for show.</p>
          <p>This style works across California.</p>
          <p>Whether the ADU is used for family, guests, or rental income, indoor outdoor flow increases how people experience the space. It feels more valuable because it is more usable.</p>
          <p>That is the difference.</p>
          <p>Not more space.</p>
          <p>Better space.</p>
        </article>
        <BlogCTAButtons />
      </main>
      <FloatingADUWizard />
      <FloatingReserveButton />
      <Footer />
    </div>
  );
};

export default BlogIndoorOutdoorADU;
